import Phaser from 'phaser';
import { audio } from '../core/AudioSynth';
import { saveManager } from '../core/SaveManager';
import { applyAudioSettings } from '../core/Settings';

/** Retro vector/CRT styling helpers shared by the command screens. */

export interface RetroPalette {
  line: number;
  fill: number;
  accent: number;
  text: string;
  dim: string;
}

export const RETRO: RetroPalette = {
  line: 0x1e2a34,
  fill: 0x0b1116,
  accent: 0x5ec8ff,
  text: '#c8d4e0',
  dim: '#6a7a88',
};

/**
 * Gives a rectangular Phaser control the shared command-deck hover treatment.
 * Keep the base fill explicit so dynamic redraws can safely restore the
 * correct state after the pointer leaves.
 */
export function addRetroButtonHover(
  button: Phaser.GameObjects.Rectangle,
  normalFill = 0x152028,
  hoverFill = 0x1d3a49,
  normalAlpha = 1,
  hoverAlpha = 1,
): Phaser.GameObjects.Rectangle {
  button.on('pointerover', () => button.setFillStyle(hoverFill, hoverAlpha));
  button.on('pointerout', () => button.setFillStyle(normalFill, normalAlpha));
  return button;
}

/** Adds compact in-canvas audio toggles for command-deck headers. */
export function addAudioToggleButtons(
  scene: Phaser.Scene,
  x: number,
  y: number,
): Phaser.GameObjects.Container {
  const root = scene.add.container(x, y).setDepth(72);

  (['music', 'voice'] as const).forEach((key, index) => {
    const label = key === 'music' ? 'MUSIC' : 'VOICE';
    const width = 26;
    const centerX = index * (width + 4);
    const bg = scene.add
      .rectangle(centerX, 0, width, 22, 0x152028, 1)
      .setOrigin(0.5)
      .setStrokeStyle(1, 0x31566a)
      .setInteractive({ useHandCursor: true });
    const text = scene.add
      .text(centerX, 0, key === 'music' ? '♪' : '◉', {
        fontFamily: 'Segoe UI Symbol, Consolas, monospace',
        fontSize: '15px',
        color: '#9ad4e8',
      })
      .setOrigin(0.5);
    let isOn = true;

    const sync = () => {
      const on = saveManager.data.settings[key];
      isOn = on;
      text.setText(key === 'music' ? '♪' : '◉');
      text.setColor(on ? '#9ad4e8' : '#68757d');
      bg.setStrokeStyle(1, on ? 0x5ec8ff : 0x3b4145);
      bg.setFillStyle(on ? 0x152028 : 0x12181c);
    };

    bg.on('pointerover', () => bg.setFillStyle(0x1d3a49));
    bg.on('pointerout', () => bg.setFillStyle(isOn ? 0x152028 : 0x12181c));
    bg.on('pointerdown', () => {
      saveManager.data.settings[key] = !saveManager.data.settings[key];
      saveManager.save();
      applyAudioSettings();
      audio.click();
      sync();
    });
    addRetroTooltip(scene, bg, () => `${label} ${onLabel(key)}  ·  click to toggle`);
    root.add([bg, text]);
    sync();
    window.addEventListener('apogee-audio-settings', sync);
    scene.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      window.removeEventListener('apogee-audio-settings', sync);
    });
  });

  return root;
}

function onLabel(key: 'music' | 'voice'): string {
  return saveManager.data.settings[key] ? 'ON' : 'OFF';
}

function addRetroTooltip(scene: Phaser.Scene, target: Phaser.GameObjects.Rectangle, message: string | (() => string)): void {
  let tip: Phaser.GameObjects.Text | undefined;
  const show = () => {
    tip?.destroy();
    const bounds = target.getBounds();
    tip = scene.add.text(bounds.centerX, bounds.bottom + 5, typeof message === 'function' ? message() : message, {
      fontFamily: 'Consolas, monospace', fontSize: '9px', color: '#d8f6ff',
      backgroundColor: '#071017', padding: { x: 6, y: 4 },
    }).setOrigin(0.5, 0).setDepth(1000);
  };
  target.on('pointerover', show);
  target.on('pointerout', () => { tip?.destroy(); tip = undefined; });
  scene.events.once(Phaser.Scenes.Events.SHUTDOWN, () => tip?.destroy());
}

/** A panel with a crisp vector outline and corner brackets. */
export function drawRetroPanel(
  g: Phaser.GameObjects.Graphics,
  x: number,
  y: number,
  w: number,
  h: number,
  colour = RETRO.line,
  alpha = 1,
  bracket = 12,
): void {
  g.fillStyle(RETRO.fill, 0.94);
  g.fillRect(x, y, w, h);
  g.lineStyle(1, colour, alpha);
  g.strokeRect(x + 0.5, y + 0.5, w - 1, h - 1);
  g.lineStyle(2, colour, alpha);
  // corner brackets
  g.lineBetween(x, y, x + bracket, y);
  g.lineBetween(x, y, x, y + bracket);
  g.lineBetween(x + w, y, x + w - bracket, y);
  g.lineBetween(x + w, y, x + w, y + bracket);
  g.lineBetween(x, y + h, x + bracket, y + h);
  g.lineBetween(x, y + h, x, y + h - bracket);
  g.lineBetween(x + w, y + h, x + w - bracket, y + h);
  g.lineBetween(x + w, y + h, x + w, y + h - bracket);
}

/** Faint horizontal CRT scanlines. */
export function drawScanlines(
  g: Phaser.GameObjects.Graphics,
  w: number,
  h: number,
  alpha = 0.05,
  gap = 3,
): void {
  g.lineStyle(1, 0x000000, alpha);
  for (let y = 0; y < h; y += gap) g.lineBetween(0, y, w, y);
}

/** A horizontal vector rule with a bright tip, used as a section divider. */
export function drawVectorRule(
  g: Phaser.GameObjects.Graphics,
  x: number,
  y: number,
  w: number,
  colour = RETRO.accent,
  alpha = 0.6,
): void {
  g.lineStyle(1, colour, alpha);
  g.lineBetween(x, y, x + w, y);
  g.fillStyle(colour, Math.min(1, alpha + 0.3));
  g.fillRect(x, y - 1, 12, 2);
}

/**
 * Adds a retro fullscreen toggle. Returns the label so callers can reposition
 * or restyle it if needed.
 */
export function addFullscreenButton(
  scene: Phaser.Scene,
  x: number,
  y: number,
  width = 132,
): Phaser.GameObjects.Text {
  const label = scene.add
    .text(x, y, '', {
      fontFamily: 'Consolas, monospace',
      fontSize: '11px',
      color: '#9ad4e8',
      backgroundColor: '#10161c',
      padding: { x: 8, y: 6 },
      align: 'center',
    })
    .setOrigin(0.5)
    .setDepth(70)
    .setInteractive({ useHandCursor: true });

  const sync = () => {
    label.setText(scene.scale.isFullscreen ? 'EXIT FULL  [F]' : 'FULLSCREEN  [F]');
    label.setFixedSize?.(width, 28);
  };
  sync();

  label.on('pointerover', () => label.setColor('#e0f4ff'));
  label.on('pointerout', () => label.setColor('#9ad4e8'));
  label.on('pointerdown', () => {
    audio.click();
    if (scene.scale.isFullscreen) scene.scale.stopFullscreen();
    else scene.scale.startFullscreen();
  });

  const ev = 'fullscreenchange';
  scene.scale.on(ev, sync);
  scene.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
    scene.scale.off(ev, sync);
  });

  return label;
}

/** Compact vector fullscreen control for space-constrained combat HUDs. */
export function addFullscreenIconButton(
  scene: Phaser.Scene,
  x: number,
  y: number,
): Phaser.GameObjects.Container {
  const bg = scene.add
    .rectangle(0, 0, 48, 30, 0x101b24, 1)
    .setStrokeStyle(1, 0x31566a)
    .setInteractive({ useHandCursor: true });
  const icon = scene.add.graphics();
  const draw = (hover = false) => {
    const colour = hover ? 0xe0f4ff : 0x9ad4e8;
    icon.clear();
    icon.lineStyle(2, colour, 1);
    const r = 8;
    icon.lineBetween(-r, -r + 5, -r, -r); icon.lineBetween(-r, -r, -r + 5, -r);
    icon.lineBetween(r, -r + 5, r, -r); icon.lineBetween(r, -r, r - 5, -r);
    icon.lineBetween(-r, r - 5, -r, r); icon.lineBetween(-r, r, -r + 5, r);
    icon.lineBetween(r, r - 5, r, r); icon.lineBetween(r, r, r - 5, r);
  };
  draw();
  addRetroButtonHover(bg, 0x101b24, 0x1d3a49);
  bg.on('pointerover', () => draw(true));
  bg.on('pointerout', () => draw(false));
  bg.on('pointerdown', () => {
    audio.click();
    if (scene.scale.isFullscreen) scene.scale.stopFullscreen();
    else scene.scale.startFullscreen();
  });
  addRetroTooltip(scene, bg, 'FULLSCREEN  ·  F');
  return scene.add.container(x, y, [bg, icon]).setDepth(70);
}

/** Compact settings control for command-deck headers. */
export function addSettingsIconButton(
  scene: Phaser.Scene,
  x: number,
  y: number,
  onClick: () => void,
): Phaser.GameObjects.Container {
  const bg = scene.add
    .rectangle(0, 0, 30, 30, 0x101b24, 1)
    .setStrokeStyle(1, 0x31566a)
    .setInteractive({ useHandCursor: true });
  const icon = scene.add
    .text(0, 0, '⚙', {
      fontFamily: 'Segoe UI Symbol, Consolas, monospace',
      fontSize: '17px',
      color: '#9ad4e8',
    })
    .setOrigin(0.5);
  addRetroButtonHover(bg, 0x101b24, 0x1d3a49);
  bg.on('pointerover', () => icon.setColor('#e0f4ff'));
  bg.on('pointerout', () => icon.setColor('#9ad4e8'));
  bg.on('pointerdown', () => {
    audio.click();
    onClick();
  });
  addRetroTooltip(scene, bg, 'SETTINGS  ·  ESC');
  return scene.add.container(x, y, [bg, icon]).setDepth(70);
}
